import React, { useState, useRef, useEffect } from 'react';
import { TextField, IconButton, Tooltip, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AISuggestionCard from './AISuggestionCard';
import styles from './Composer.module.css';

/**
 * Campo de digitação: multiline (Enter envia, Shift+Enter quebra linha),
 * botão de enviar e sugestão de resposta por IA em modo copiloto.
 *
 * onRequestAISuggestion(done): o pai busca a sugestão no servidor e chama
 * done(suggestion) em caso de sucesso ou done(null, erro) em caso de falha.
 * suggestion = { id, reply, notes, lastMessageId }.
 * A sugestão aparece em um card e a secretária decide: enviar, editar,
 * regenerar ou descartar. Nada é enviado automaticamente.
 *
 * onSuggestionOutcome(id, outcome, sentText) registra o desfecho
 * (sent | edited | discarded) para medir a qualidade das sugestões.
 *
 * lastMessageId: maior id da conversa; quando passa do id que a sugestão
 * viu, o card avisa que chegou mensagem nova.
 *
 * onRequestAISuggestion vem undefined quando o Assistente de IA não está
 * disponível para o usuário (ver WhatsappMessages.js); o botão fica oculto.
 */
const Composer = ({ onSend, onRequestAISuggestion, onSuggestionOutcome, disabled, chatKey, lastMessageId }) => {
  const [text, setText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiError, setAiError] = useState(null);
  const inputRef = useRef(null);
  // Sugestão levada para o campo de digitação: ao enviar, registra como editada
  const editingSuggestionRef = useRef(null);

  // Limpa campo e sugestão ao trocar de conversa
  useEffect(() => {
    setText('');
    setAiLoading(false);
    setAiSuggestion(null);
    setAiError(null);
    editingSuggestionRef.current = null;
  }, [chatKey]);

  const reportOutcome = (suggestion, outcome, sentText) => {
    if (suggestion && suggestion.id && onSuggestionOutcome) {
      onSuggestionOutcome(suggestion.id, outcome, sentText);
    }
  };

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText('');

    const editing = editingSuggestionRef.current;
    if (editing) {
      reportOutcome(editing, trimmed === editing.reply.trim() ? 'sent' : 'edited', trimmed);
      editingSuggestionRef.current = null;
    }

    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const requestSuggestion = () => {
    if (aiLoading || !onRequestAISuggestion) return;
    // Regenerar descarta a sugestão atual
    reportOutcome(aiSuggestion, 'discarded');
    setAiLoading(true);
    setAiSuggestion(null);
    setAiError(null);
    onRequestAISuggestion((suggestion, error) => {
      setAiLoading(false);
      if (suggestion && suggestion.reply) {
        setAiSuggestion(suggestion);
      } else {
        setAiError(error || 'Não foi possível gerar uma sugestão agora. Tente novamente.');
      }
    });
  };

  const clearSuggestion = () => {
    setAiSuggestion(null);
    setAiError(null);
    setAiLoading(false);
  };

  const handleDiscardSuggestion = () => {
    reportOutcome(aiSuggestion, 'discarded');
    clearSuggestion();
  };

  const handleSendSuggestion = () => {
    if (!aiSuggestion) return;
    onSend(aiSuggestion.reply);
    reportOutcome(aiSuggestion, 'sent');
    clearSuggestion();
  };

  // Leva a sugestão para o campo sem apagar o que a atendente já digitou
  const handleEditSuggestion = () => {
    if (!aiSuggestion) return;
    const draft = text.trim();
    setText(draft ? `${draft}\n\n${aiSuggestion.reply}` : aiSuggestion.reply);
    editingSuggestionRef.current = aiSuggestion;
    clearSuggestion();
    if (inputRef.current) inputRef.current.focus();
  };

  const isStale = Boolean(
    aiSuggestion && aiSuggestion.lastMessageId && lastMessageId && lastMessageId > aiSuggestion.lastMessageId
  );

  return (
    <div className={styles.wrapper}>
      <AISuggestionCard
        loading={aiLoading}
        error={aiError}
        suggestion={aiSuggestion ? aiSuggestion.reply : null}
        notes={aiSuggestion ? aiSuggestion.notes : null}
        stale={isStale}
        onSendSuggestion={handleSendSuggestion}
        onEdit={handleEditSuggestion}
        onRegenerate={requestSuggestion}
        onDiscard={handleDiscardSuggestion}
      />

      <div className={styles.inputRow}>
        {onRequestAISuggestion && (
          <Tooltip title="Sugerir resposta com IA">
            <span>
              <IconButton
                onClick={requestSuggestion}
                disabled={disabled || aiLoading}
                className={styles.aiButton}
              >
                {aiLoading ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <TextField
          fullWidth
          multiline
          maxRows={5}
          variant="outlined"
          size="small"
          placeholder="Digite sua mensagem... (Shift+Enter quebra linha)"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          inputRef={inputRef}
        />
        <Tooltip title="Enviar">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={disabled || !text.trim()}
              className={styles.sendButton}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default Composer;
